import store from "../redux";

/**
 * Demo account support.
 *
 * When the logged in user is the demo mobile number below, every savings related
 * system value / graph is fed dummy data that works out to a saving somewhere
 * between DEMO_SAVINGS_MIN_PERCENTAGE and DEMO_SAVINGS_MAX_PERCENTAGE instead of
 * the device's real (often zero) savings.
 * Everything else (consumption, money spent, voltage, ...) stays real.
 *
 * The percentage is picked from a hash of (deviceId, date) rather than at random, so a
 * given device keeps the exact same percentage for the whole of a given day - across
 * every hour slot, every graph and every re-render / refetch - while still varying from
 * day to day and from device to device.
 */
export const DEMO_SAVINGS_MOBILE = "8791644000";
export const DEMO_SAVINGS_MIN_PERCENTAGE = 8;
export const DEMO_SAVINGS_MAX_PERCENTAGE = 15;

// Percentages are picked in steps of 0.01 so they look organic instead of round numbers.
const PERCENTAGE_STEPS =
  Math.round((DEMO_SAVINGS_MAX_PERCENTAGE - DEMO_SAVINGS_MIN_PERCENTAGE) * 100) + 1;

// `mobile` is optional - pass it when the login profile is not in the store yet
// (the house is fetched before the login details are dispatched).
export const isDemoSavingsUser = (mobile) => {
  // store is optional-chained because this module is also reached from the store's own
  // reducer, where the store binding is not initialised yet.
  const number = mobile || store?.getState?.()?.login?.profile?.mobile;

  if (!number) return false;

  return String(number).replace(/\D/g, "").endsWith(DEMO_SAVINGS_MOBILE);
};

// Seed for a device on a single day. Phases of the same device share it so the device
// level total lands on exactly the same percentage as each of its phases.
export const demoSavingsSeed = (deviceId, date) => `${deviceId ?? ""}|${date ?? ""}`;

// FNV-1a, chosen only because it is short and spreads similar seeds (consecutive dates,
// consecutive device ids) far apart.
const hashSeed = (seed) => {
  const text = String(seed ?? "");
  let hash = 0x811c9dc5;

  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }

  return hash >>> 0;
};

// Stable percentage in [DEMO_SAVINGS_MIN_PERCENTAGE, DEMO_SAVINGS_MAX_PERCENTAGE] for a seed.
export const demoSavingsPercentage = (seed) =>
  DEMO_SAVINGS_MIN_PERCENTAGE + (hashSeed(seed) % PERCENTAGE_STEPS) / 100;

// Savings are always shown as saved / (consumed + saved). Deriving the saved value as
// consumed * ratio makes that expression resolve to exactly the seed's percentage.
const savedToConsumedRatio = (seed) => {
  const percentage = demoSavingsPercentage(seed);

  return percentage / (100 - percentage);
};

const applyRatio = (consumed, ratio) => {
  const value = Number(consumed) * ratio;

  return isNaN(value) || !isFinite(value) ? 0 : value;
};

// Saved value that corresponds to the seed's percentage of the given consumption.
export const demoSavedFromConsumed = (consumed, seed) =>
  applyRatio(consumed, savedToConsumedRatio(seed));

// Same thing for a bar/line series, so the savings graphs follow the shape of consumption.
// The ratio is resolved once for the series, so every slot of the day shares one percentage.
export const demoSavedSeriesFromConsumed = (consumedSeries, seed) => {
  const ratio = savedToConsumedRatio(seed);

  return (consumedSeries || []).map((value) => applyRatio(value, ratio));
};
